
import { PlatformInfo } from './types';

export class PlatformData {
  // Major B2C retailers that should always be treated as business-to-consumer
  private static readonly B2C_RETAILERS = [
    'amazon', 'walmart', 'target', 'best buy', 'bestbuy', 'wayfair', 'newegg', 
    'macys', "macy's", 'nordstrom', 'kohls', "kohl's", 'home depot', 'homedepot',
    'lowes', "lowe's", 'costco', 'apple', 'dell', 'nike', 'adidas', 'sephora', 
    'ulta beauty', 'ulta', 'etsy', 'ebay'
  ];

  static getPlatformSpecificData(
    platform: PlatformInfo['platform'], 
    isPrivateSeller: boolean
  ): PlatformInfo['platformSpecificData'] {
    const baseData = {
      supportsDisputes: true,
      hasSellerProtection: false,
      recommendedApproach: 'direct_contact' as const,
      escalationPath: ['merchant_support', 'payment_provider'],
      hasMarketplaceProtection: false,
      refundPolicyDays: 30,
      disputeProcessAvailable: false,
      chargebackRights: true,
      platformFees: false
    };

    // Platform-specific configurations
    switch (platform) {
      case 'amazon':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: !isPrivateSeller,
          recommendedApproach: isPrivateSeller ? 'direct_contact' : 'platform_mediation',
          escalationPath: isPrivateSeller 
            ? ['seller_direct', 'amazon_support', 'payment_provider']
            : ['amazon_support', 'payment_provider'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false
        };
      
      case 'walmart':
      case 'target':
      case 'bestbuy':
      case 'costco':
      case 'apple':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'direct_contact',
          escalationPath: ['customer_service', 'corporate_escalation', 'payment_provider'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false
        };
      
      case 'wayfair':
      case 'newegg':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'direct_contact',
          escalationPath: ['customer_service', 'payment_provider'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false
        };
      
      case 'macys':
      case 'nordstrom':
      case 'kohls':
      case 'sephora':
      case 'ulta':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'direct_contact',
          escalationPath: ['customer_service', 'corporate_escalation', 'payment_provider'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false
        };
      
      case 'homedepot':
      case 'lowes':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'direct_contact',
          escalationPath: ['customer_service', 'store_manager', 'corporate_escalation', 'payment_provider'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 90,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false
        };
      
      case 'dell':
      case 'nike':
      case 'adidas':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'direct_contact',
          escalationPath: ['customer_service', 'payment_provider'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false
        };
      
      case 'ebay':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['ebay_resolution', 'payment_provider'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: true
        };
      
      case 'etsy':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'direct_contact',
          escalationPath: ['seller_direct', 'etsy_support', 'payment_provider'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 14,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: true
        };
      
      case 'shopify':
        return {
          ...baseData,
          supportsDisputes: false,
          recommendedApproach: 'direct_contact',
          escalationPath: ['merchant_support', 'payment_provider'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 30,
          disputeProcessAvailable: false,
          chargebackRights: true,
          platformFees: false
        };
      
      case 'paypal':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['paypal_resolution'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 180,
          disputeProcessAvailable: true,
          chargebackRights: false,
          platformFees: false
        };

      // Subscription Platforms
      case 'apple_app_store':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'self_service_portal' as const,
          escalationPath: ['Self-service refund portal', 'Apple Support'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 2,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://reportaproblem.apple.com/',
          subscriptionType: 'monthly' as const,
          refundEligibilityCriteria: ['Refund requested within 48 hours of charge', 'Unintentional renewal or forgotten cancellation']
        };

      case 'google_play':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'self_service_portal' as const,
          escalationPath: ['Google Play refund portal', 'Google Support'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 2,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://play.google.com/store/account',
          subscriptionType: 'monthly' as const,
          refundEligibilityCriteria: ['Request within 48 hours of purchase', 'App malfunction or unauthorized charge']
        };

      case 'netflix':
        return {
          ...baseData,
          supportsDisputes: false,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['Netflix customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 0,
          disputeProcessAvailable: false,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://help.netflix.com/en/contactus',
          subscriptionType: 'monthly' as const,
          refundEligibilityCriteria: ['Unauthorized charge', 'Forgot to cancel trial']
        };

      case 'spotify':
        return {
          ...baseData,
          supportsDisputes: false,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['Spotify customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 0,
          disputeProcessAvailable: false,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://support.spotify.com/contact-spotify-support/',
          subscriptionType: 'monthly' as const,
          refundEligibilityCriteria: ['Unauthorized charge', 'Trial expired without notice']
        };

      case 'youtube_premium':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'platform_mediation' as const,
          escalationPath: ['YouTube support'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 0,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://support.google.com/youtube/answer/6305537',
          subscriptionType: 'monthly' as const,
          refundEligibilityCriteria: ['Billing error', 'Service failure']
        };

      case 'adobe_creative_cloud':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['Adobe customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 14,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://helpx.adobe.com/manage-account/using/cancel-subscription.html',
          subscriptionType: 'annual' as const,
          refundEligibilityCriteria: ['Cancellation within 14 days', 'Mistaken subscription']
        };

      case 'canva_pro':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['Canva customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 14,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://www.canva.com/help/article/request-refund',
          subscriptionType: 'annual' as const,
          refundEligibilityCriteria: ['First-time upgrade', 'Trial expired']
        };

      case 'notion':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['Notion customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 3,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://www.notion.so/help/contact-us',
          subscriptionType: 'annual' as const,
          refundEligibilityCriteria: ['Annual plan upgrade', 'Recent charge']
        };

      case 'grammarly':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['Grammarly customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 10,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://support.grammarly.com',
          subscriptionType: 'annual' as const,
          refundEligibilityCriteria: ['Refund requested within 10 days', 'Mistaken upgrade']
        };

      case 'squarespace':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['Squarespace customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 14,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://support.squarespace.com/hc/en-us/articles/205812378',
          subscriptionType: 'annual' as const,
          refundEligibilityCriteria: ['Annual plan cancelation within 14 days']
        };

      case 'nordvpn':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['NordVPN customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://support.nordvpn.com',
          subscriptionType: 'annual' as const,
          refundEligibilityCriteria: ['Within 30 days', 'No usage abuse']
        };

      case 'expressvpn':
        return {
          ...baseData,
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact' as const,
          escalationPath: ['ExpressVPN customer service'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://www.expressvpn.com/support/',
          subscriptionType: 'annual' as const,
          refundEligibilityCriteria: ['Within 30 days', 'Valid transaction']
        };

      case 'upwork':
        return {
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['Dispute Resolution', 'Customer Support', 'Arbitration'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 14,
          disputeProcessAvailable: true,
          chargebackRights: false,
          platformFees: true,
          disputeChannel: 'https://support.upwork.com',
          disputeTimeframe: '14 days after milestone submission',
          refundEligibilityCriteria: ['Dispute filed within 14 days of milestone submission', 'Work not delivered as expected', 'Hourly time was not tracked properly']
        };

      case 'fiverr':
        return {
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['Resolution Center', 'Customer Support', 'Mediation'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 13,
          disputeProcessAvailable: true,
          chargebackRights: false,
          platformFees: true,
          disputeChannel: 'https://www.fiverr.com/support',
          disputeTimeframe: '13 days after order delivery',
          refundEligibilityCriteria: ['Dispute initiated within 13 days', 'Work not as described or incomplete']
        };

      case 'freelancer':
        return {
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['Dispute System', 'Customer Support', 'Arbitration'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 30,
          disputeProcessAvailable: true,
          chargebackRights: false,
          platformFees: true,
          disputeChannel: 'https://support.freelancer.com',
          disputeTimeframe: 'Variable based on project terms',
          refundEligibilityCriteria: ['Project delivery not complete', 'Mutual agreement not reached']
        };

      case 'peopleperhour':
        return {
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['Dispute Resolution', 'Moderation Team', 'Customer Support'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 7,
          disputeProcessAvailable: true,
          chargebackRights: false,
          platformFees: true,
          disputeChannel: 'https://support.peopleperhour.com',
          disputeTimeframe: '7 days after delivery',
          refundEligibilityCriteria: ['Dispute raised within 7 days of delivery', 'Work failed to meet expectations']
        };

      case 'taskrabbit':
        return {
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['Customer Support', 'Task Dispute Team', 'Resolution Specialist'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 1,
          disputeProcessAvailable: true,
          chargebackRights: false,
          platformFees: true,
          disputeChannel: 'https://support.taskrabbit.com',
          disputeTimeframe: '24 hours after task completion',
          refundEligibilityCriteria: ['Dispute submitted within 24 hours of task completion', 'Task not completed as agreed']
        };

      // Event & Ticketing platforms
      case 'ticketmaster':
        return {
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'self_service_portal',
          escalationPath: ['Ticketmaster help center', 'Customer service', 'Credit card dispute'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 0, // Only if event canceled
          disputeProcessAvailable: false,
          chargebackRights: true,
          platformFees: true,
          autoRefundChannel: 'https://help.ticketmaster.com',
          refundEligibilityCriteria: ['Event was canceled', 'Ticket not transferred or used']
        };

      case 'stubhub':
        return {
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['StubHub support', 'FanProtect claim', 'Payment dispute'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 0, // Only if event canceled
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: true,
          autoRefundChannel: 'https://support.stubhub.com',
          refundEligibilityCriteria: ['Event officially canceled', 'User has FanProtect or ticket was invalid']
        };

      case 'eventbrite':
        return {
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact',
          escalationPath: ['Contact event organizer', 'Eventbrite support', 'Payment dispute'],
          hasMarketplaceProtection: false,
          refundPolicyDays: 0, // Organizer dependent
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: true,
          autoRefundChannel: 'https://www.eventbrite.com/support',
          refundEligibilityCriteria: ['Organizer allows refunds', 'Event canceled or didn\'t occur']
        };

      case 'seatgeek':
        return {
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['SeatGeek help', 'Swaps program', 'Payment dispute'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 0, // Only if event canceled
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: true,
          autoRefundChannel: 'https://seatgeek.com/help',
          refundEligibilityCriteria: ['Event canceled', 'User eligible for SeatGeek Swaps']
        };

      // Accommodation platforms
      case 'airbnb':
        return {
          supportsDisputes: true,
          hasSellerProtection: true,
          recommendedApproach: 'platform_mediation',
          escalationPath: ['Contact host', 'Airbnb resolution center', 'Payment dispute'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 1, // Depends on cancellation policy
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: true,
          autoRefundChannel: 'https://www.airbnb.com/help',
          refundEligibilityCriteria: ['Canceled within cancellation window', 'Host failed to deliver accommodation', 'Safety or misrepresentation issue']
        };

      case 'booking':
        return {
          supportsDisputes: true,
          hasSellerProtection: false,
          recommendedApproach: 'direct_contact',
          escalationPath: ['Contact property', 'Booking.com support', 'Payment dispute'],
          hasMarketplaceProtection: true,
          refundPolicyDays: 1, // Varies by property
          disputeProcessAvailable: true,
          chargebackRights: true,
          platformFees: false,
          autoRefundChannel: 'https://www.booking.com/help',
          refundEligibilityCriteria: ['Canceled within allowed timeframe', 'Property failed to meet description']
        };

      default:
        return baseData;
    }
  }

  static isB2CRetailer(merchantName: string): boolean {
    const normalizedName = merchantName.toLowerCase().trim();
    
    return this.B2C_RETAILERS.some(retailer => {
      // Check for exact match or if the merchant name contains the retailer name
      return normalizedName === retailer || 
             normalizedName.includes(retailer) ||
             // Handle cases like "Best Buy Store" or "Target Corporation"
             normalizedName.startsWith(retailer) ||
             // Handle cases with common business suffixes
             normalizedName.replace(/\s+(inc|corp|corporation|llc|ltd|store|shop)$/i, '') === retailer;
    });
  }
}
